import { PAYMENT_MODULE } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback } from "react";
import { useAPIUtils } from "../../useAPIUtils";

AxiosCurlirize(axios, () => {});
type ICartAPIResponse = { status: boolean; message: string; payment_status: string; image_base64?: string | null;expires_at?: number | null;};

export const localizeQRExpiry = (item: any) =>
  item?.server_now ? { ...item, expires_at: Date.now() / 1000 + item.expires_at - item.server_now } : item;

export interface IPOSProps {
  user_id: string;
  clinic_id: string;
  amount: number;
  payment_mode: string;
  invoice_ids: Array<string | { invoice_id: string; amount: number }>;
  source: string;
  mobile_number: number;
  machine_id: string;
  /** Omit (undefined) = auto-fetch wallet balance. 0 = skip wallet. >0 = use this exact amount. */
  wallet_amount_consume?: number;
}

const ONLINE_PAYMENT_RETRIES = 3;
const ONLINE_PAYMENT_RETRY_DELAY_MS = 400;

// Names the payment so a repeat click is not a second link. Advance collection and POS
// send none: both can legitimately repeat, and a terminal must not get a replayed txn.
const idempotencyKeyFor = (body: IPOSProps): string | undefined => {
  if (body.payment_mode !== "PAYMENT_LINK" || !body.invoice_ids?.length) return undefined;
  const invoices = [...body.invoice_ids].sort().join(":");
  return `vms:${invoices}:${Math.round(body.amount * 100)}:${Math.round((body.wallet_amount_consume ?? 0) * 100)}`;
};
export const useOnlinePaymentPostAPI = () => {
  const configuration = useConfiguration();

  const dispatch = useAppDispatch();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  // isCustomerWalletDetailsInProgress
  const execute = useCallback(
    async (paramsBody: IPOSProps): Promise<ICartAPIResponse | undefined> => {
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const body = { ...paramsBody, idempotency_key: idempotencyKeyFor(paramsBody) };
        const headers = { Authorization: `${accessToken}`, "Content-Type": "application/json" };

        // 409 means another request holds this idempotency key; retrying picks up its result.
        for (let attempt = 0; attempt < ONLINE_PAYMENT_RETRIES; attempt += 1) {
          try {
            const res: AxiosResponse<any> = await axios.post(`${PAYMENT_MODULE?.ONLINE_PAYMENT_API?.baseURL}`, body, { headers });
            if (res.data.status === true) {
              return {
                status: true,
                message: res.data?.data?.order_id,
                payment_status: res.data?.data?.order_status,
                image_base64: res.data?.data?.image_base64 ?? null,
                expires_at: localizeQRExpiry(res.data?.data ?? {}).expires_at ?? null,
              };
            }
            return { status: false, message: null, payment_status: null, image_base64: null };
          } catch (e) {
            const error = e as AxiosError;
            if (error.response?.status === 409 && attempt < ONLINE_PAYMENT_RETRIES - 1) {
              await new Promise((resolve) => setTimeout(resolve, ONLINE_PAYMENT_RETRY_DELAY_MS));
              continue;
            }
            sendCurl(error.config?.["curlCommand"]);
            captureError(error);
            return { status: false, message: null, payment_status: null, image_base64: null };
          }
        }
        return { status: false, message: null, payment_status: null, image_base64: null };
      } catch (e: any) {
        captureError(e);
      }
    },
    [dispatch],
  );
  return { execute };
};
